import { NgZone } from '@angular/core';

/**
 * Keeps `anchor` at its current on-screen position inside `scrollContainer` for `durationMs`
 * (e.g. while content above it animates its height), by correcting `scrollContainer.scrollTop`
 * every frame by however much the anchor has drifted. Runs outside Angular — it only writes
 * scrollTop, nothing to change-detect.
 *
 * Returns a function that stops the correction early (call it on a new start or on destroy).
 */
export function keepElementInPlace(
  scrollContainer: HTMLElement,
  anchor: Element,
  durationMs: number,
  ngZone: NgZone,
): () => void {
  const anchorTop = anchor.getBoundingClientRect().top;
  const startedAt = performance.now();
  let rafId: number = null;

  ngZone.runOutsideAngular(() => {
    const step = () => {
      const drift = anchor.getBoundingClientRect().top - anchorTop;
      if (Math.abs(drift) >= 0.5) {
        scrollContainer.scrollTop += drift;
      }
      rafId = (performance.now() - startedAt < durationMs) ? requestAnimationFrame(step) : null;
    };
    rafId = requestAnimationFrame(step);
  });

  return () => {
    if (rafId !== null) {
      cancelAnimationFrame(rafId);
      rafId = null;
    }
  };
}
